import React from 'react';
import { ClipboardCheckIcon, FileTextIcon, CoinsIcon, CalendarIcon, CheckSquareIcon, EyeIcon, ListChecksIcon, HandshakeIcon, ShieldCheckIcon } from 'lucide-react';
export const ProcessSection = () => {
  const steps = [{
    id: 1,
    title: 'Preparation (Now - April 30, 2026)',
    description: 'Start preparing now by researching your desired gTLD, assembling your team, and gathering technical, operational, and financial documentation. Early preparation is key to avoiding issues later.',
    icon: <ClipboardCheckIcon className="h-8 w-8 text-[#5A5535]" data-id="element-295" />
  }, {
    id: 2,
    title: 'Application Submission (April 30 - August 12, 2026)',
    description: "Complete and submit your application through ICANN's TLD Application System (TAS) during the 2026 application window.",
    icon: <FileTextIcon className="h-8 w-8 text-[#5A5535]" data-id="element-296" />
  }, {
    id: 3,
    title: 'Application Fee Due (May 7, 2026)',
    description: 'Pay the required evaluation fee of $227,000 USD to ICANN. This fee covers the cost of evaluating your application. If you choose to withdraw your application, a portion of the fee may be refundable depending on the timing of your withdrawal; however, no more than 65% ($147,550) will be refunded, regardless of when the withdrawal occurs.',
    icon: <CoinsIcon className="h-8 w-8 text-[#5A5535]" data-id="element-297" />
  }, {
    id: 4,
    title: 'Reveal Day (Mid-October 2026)',
    description: 'ICANN expects to publish the list of all applications that have passed the Administrative Check on Reveal Day no later than nine weeks following the close of the application submission period. After Reveal Day, applicants will have 14 days to decide whether to stick with their primary string or switch to their alternate string (if they included one in their application and it is not in contention).',
    icon: <EyeIcon className="h-8 w-8 text-[#5A5535]" data-id="element-298" />
  }, {
    id: 5,
    title: 'String Confirmation Day (14 days after Reveal Day)',
    description: 'The list of applied for strings and the final composition of contention sets (when more than one applicant has applied for the same string or confusingly similar strings) are set.',
    icon: <ListChecksIcon className="h-8 w-8 text-[#5A5535]" data-id="element-299" />
  }, {
    id: 6,
    title: 'Evaluation Period (180 Days)',
    description: <>
          String Evaluation is expected to take 180 days and includes five
          parallel review components:
          <ul className="list-disc list-inside mt-3 space-y-1.5" data-id="element-300">
            <li data-id="element-301">String Similarity Evaluation</li>
            <li data-id="element-302">Name Collision Initial Assessment</li>
            <li data-id="element-303">Safeguard Assessment</li>
            <li data-id="element-304">Geographic Names Identification</li>
            <li data-id="element-305">Singular/Plural Notifications Evaluation</li>
          </ul>
        </>,
    icon: <CalendarIcon className="h-8 w-8 text-[#5A5535]" data-id="element-306" />
  }, {
    id: 7,
    title: 'Contracting',
    description: 'Upon successful completion of evaluation, applicants enter into a Registry Agreement with ICANN. This agreement outlines the terms and conditions under which the new gTLD registry will operate.',
    icon: <HandshakeIcon className="h-8 w-8 text-[#5A5535]" data-id="element-307" />
  }, {
    id: 8,
    title: 'Pre-Delegation Testing',
    description: 'Before the new gTLD can be delegated into the root zone, the applicant must pass pre-delegation testing to verify that the registry infrastructure meets all technical requirements for stable and secure operation.',
    icon: <ShieldCheckIcon className="h-8 w-8 text-[#5A5535]" data-id="element-308" />
  }, {
    id: 9,
    title: 'Delegation & Launch',
    description: 'If approved, complete final technical requirements and launch your new gTLD, at which time it gets added into the official root zone. Begin operating your registry and offering domain registrations. Note that gTLDs must be delegated within one (1) year of executing a Registry Agreement.',
    icon: <CheckSquareIcon className="h-8 w-8 text-[#5A5535]" data-id="element-309" />
  }];
  return <section id="process" className="py-20 bg-white" data-id="element-310">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-311">
        <div className="text-center mb-20" data-id="element-312">
          <h2 className="text-3xl md:text-5xl font-bold text-[#1E1D14] mb-6 font-serif" data-id="element-313">
            2026 Application Process
          </h2>
          <div className="w-24 h-1 bg-[#C4A44A] mx-auto mb-6" data-id="element-314"></div>
          <p className="text-xl text-[#5A5535] max-w-3xl mx-auto font-light" data-id="element-315">
            Applying for a new gTLD in the 2026 round involves several steps and
            careful planning. Start preparing now to maximize your chances of
            success.
          </p>
        </div>

        <div className="relative" data-id="element-316">
          {/* Vertical line for desktop */}
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-[#D4C88A] transform -translate-x-1/2" data-id="element-317"></div>
          <div className="space-y-16 relative" data-id="element-318">
            {steps.map((step, index) => <div key={step.id} className={`md:flex ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`} data-id="element-319">
                <div className="md:w-1/2" data-id="element-320"></div>
                <div className="mx-auto md:mx-0 z-10 flex items-center justify-center w-14 h-14 rounded-full bg-[#5A5535] border-4 border-[#F7F5EE] shadow-lg" data-id="element-321">
                  <span className="text-white font-bold text-lg font-serif" data-id="element-322">
                    {step.id}
                  </span>
                </div>
                <div className={`md:w-1/2 bg-[#FAF9F5] p-8 rounded-sm shadow-sm border border-[#EDE9DB] mt-6 md:mt-0 hover:border-[#C4A44A] transition-colors ${index % 2 === 0 ? 'md:ml-12' : 'md:mr-12'}`} data-id="element-323">
                  <div className="flex items-center mb-5" data-id="element-324">
                    <div className="mr-5 p-3 bg-[#EDE9DB] rounded-full" data-id="element-325">
                      {step.icon}
                    </div>
                    <h3 className="text-xl font-bold text-[#1E1D14] font-serif" data-id="element-326">
                      {step.title}
                    </h3>
                  </div>
                  <div className="text-[#3D3A25] leading-relaxed" data-id="element-327">
                    {step.description}
                  </div>
                </div>
              </div>)}
          </div>
        </div>

        <div className="mt-20 text-center" data-id="element-328">
          <a href="#contact" className="inline-flex items-center px-8 py-4 border border-transparent text-sm font-bold uppercase tracking-wider rounded-sm shadow-md text-white bg-[#C4A44A] hover:bg-[#B39540] transition-colors" data-id="element-329">
            Get Assistance From Experienced Counsel
          </a>
          <p className="mt-6 text-sm text-[#5A5535]" data-id="element-330">
            Our team of highly experienced practitioners can guide you through
            each step of the 2026 application process.
          </p>
        </div>
      </div>
    </section>;
};